<template>
    <form
        v-if="!requestStatus.success"
        @submit.prevent="onSubmit"
    >
        <div>
            <div class="row">
                <div class="col-default-12">
                    <z-select
                        v-model="formValues.type"
                        :options="selectOptions"
                        :placeholder="formTextLocalize.type"
                        ref="typeEl"
                        required
                    />
                </div>
            </div>
        </div>

        <div class="u-mt-m" v-show="formValues.type === selectOptionsEnum.group">
            <div class="row">
                <div class="col-default-12">
                    <z-input
                        v-model="formValues.group.name"
                        :placeholder="formTextLocalize.group.name"
                        name="groupName"
                        ref="groupNameEl"
                        clearable
                        required
                    />
                </div>


                <div class="col-default-12">
                    <z-input
                        v-model="formValues.group.quantity"
                        :placeholder="formTextLocalize.group.quantity"
                        name="groupQuantity"
                        ref="groupQuantityEl"
                        type="number"
                        min-val="1"
                        max-val="19"
                        required
                        clearable
                    />

                    <p class="u-pt-2xs text-size-s">{{ formTextLocalize.group.quantityDescription }}</p>
                </div>
            </div>
        </div>

        <div class="u-mt-m" v-show="formValues.type === selectOptionsEnum.special">
            <div class="row">
                <div class="col-default-12">
                    <z-input
                        v-model="formValues.special.fio"
                        :placeholder="formTextLocalize.special.fio"
                        name="specialFio"
                        ref="specialFioEl"
                        clearable
                        required
                    />
                </div>
            </div>
        </div>

        <div class="u-mt-m">
            <div class="row u-pb-2xs">
                <div class="col-default-12">
                    <z-input
                        v-model="formValues.numberOfMinors"
                        :placeholder="formTextLocalize.numberOfMinors"
                        name="numberOfMinors"
                        ref="numberOfMinorsEl"
                        type="number"
                        min-val="1"
                        max-val="19"
                        clearable
                    />
                </div>
            </div>

            <p class="u-collapse--all text-size-s" v-html="formTextLocalize.numberOfMinorsDescription"></p>
        </div>

        <div class="u-mt-m">
            <div class="row">
                <div class="col-default-12">
                    <z-date-picker
                        v-model="formValues.date"
                        :placeholder="formTextLocalize.date"
                        :range="true"
                        name="date"
                        ref="dateEl"
                        required
                    />
                </div>
            </div>
        </div>

        <div class="u-mt-m">
            <div class="row u-pb-2xs">
                <div class="col-default-12">
                    <z-input
                        v-model="formValues.city"
                        :placeholder="formTextLocalize.city"
                        name="city"
                        ref="cityEl"
                        clearable
                        required
                    />
                </div>
            </div>

            <p class="u-collapse--all text-size-s" v-html="formTextLocalize.cityDescription"></p>
        </div>

        <div class="u-mt-m">
            <div class="row">
                <div class="col-default-6 col-v-tablet-12">
                    <z-input
                        v-model="formValues.phone"
                        :placeholder="formTextLocalize.phone"
                        :mask="{mask: '+{7} (000) 000-00-00'}"
                        name="phone"
                        ref="phoneEl"
                        type="tel"
                        clearable
                        required
                    />
                </div>

                <div class="col-default-6 col-v-tablet-12">
                    <z-input
                        v-model="formValues.email"
                        :placeholder="formTextLocalize.email"
                        name="email"
                        ref="emailEl"
                        type="email"
                        clearable
                        required
                    />
                </div>
            </div>
        </div>

        <div class="u-mt-m">
            <div class="row">
                <div class="col-default-12">
                    <z-checkbox
                        :data="{
                            text: formTextLocalize.personalDataAgree,
                            id: 'personalDataAgree'
                        }"
                        size="m"
                        name="personalDataAgree"
                        ref="personalDataAgreeEl"
                        required
                    />

                    <p class="u-mt-2xs text-size-s">
                        Поля помеченные * обязательны для заполнения
                    </p>
                </div>
            </div>
        </div>

        <div class="row u-pt-2xs">
            <div class="col-default-4 col-v-tablet-12">
                <z-button :loading="requestStatus.loading" ref="submit" type="submit">{{ formTextLocalize.submit }}</z-button>

                <p v-if="!requestStatus.loading && requestStatus.error.message !== null" class="u-mt-2xs border-error-text">{{ requestStatus.error.message }}</p>
            </div>

        </div>
    </form>

    <p v-else>Данные успешно отправлены</p>
</template>

<script setup>
import { computed, reactive, ref, onMounted } from 'vue';
import { sendIndustrialTourism } from '@/api/industrial-tourism';
import { useRecaptcha } from '@/composable/useRecaptcha'

const { initGRecaptcha, getRecaptchaToken } = useRecaptcha()

defineProps({
    formTextLocalize: Object
})

const selectOptionsEnum = {
    group: 'Групповая',
    special: 'Индивидуальная'
}

const typeEl = ref(null)
const groupNameEl = ref(null)
const groupQuantityEl = ref(null)
const specialFioEl = ref(null)
const numberOfMinorsEl = ref(null)
const dateEl = ref(null)
const cityEl = ref(null)
const phoneEl = ref(null)
const emailEl = ref(null)
const personalDataAgreeEl = ref(null)

const requestStatus = reactive({
    responsed: false,
    loading: false,
    error: {
        message: null
    }
})

const formValues = reactive({
    type: selectOptionsEnum.group,
    group: {
        name: '',
        quantity: ''
    },
    special: {
        fio: ''
    },
    numberOfMinors: '',
    date: '',
    city: '',
    phone: '',
    email: ''
})

const requiredFields = computed(() => [
    typeEl,
    ...(
        formValues.type === selectOptionsEnum.group ? [groupNameEl, groupQuantityEl] : [specialFioEl]
    ),
    numberOfMinorsEl,
    dateEl,
    cityEl,
    phoneEl,
    emailEl,
    personalDataAgreeEl
]);

const selectOptions = computed(() => Object.values(selectOptionsEnum).map((text, i) => ({
    'id': text,
    'selected': i === 0,
    'disabled': false,
    'text': text,
})))

const formFilter = ([formKey]) => !selectOptionsEnum.hasOwnProperty(formKey) || selectOptionsEnum[formKey] === formValues.type

const createFormData = (data) => {
    return Object.entries(data)
        .filter(formFilter)
        .reduce((formData, [key, value]) => {
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                    if (!nestedValue) return;

                    formData.append(`${key}[${nestedKey}]`, nestedValue)
                })
            } else if (value !== '') {
                formData.append(key, value)
            }

            return formData
        }, new FormData())
}

const onSubmit = async () => {
    let isValid = true

    requiredFields.value.forEach(item => {
        if (item.value) {
            item.value?.validate()
            if (item.value.error) {
                isValid = false
            }
        }
    })

    if (!isValid) return

    try {
        const recaptchaToken = await getRecaptchaToken()

        if (!recaptchaToken) {
            throw new Error('Failed to get reCAPTCHA token');
        }

        formValues.captcha = recaptchaToken
        sendForm()
    } catch (error) {
        console.error(error)
    }
}


const sendForm = () => {
    requestStatus.loading = true
    requestStatus.error.message = null

    sendIndustrialTourism(createFormData(formValues))
        .then(data => {
            if (data.status === 'error') {
                requestStatus.success = false
                requestStatus.error.message = data.message
            } else {
                requestStatus.success = true
            }

        })
        .catch(e => {
            requestStatus.error.message = e
            requestStatus.success = false
        })
        .finally(() => {
            requestStatus.loading = false
        })
}

onMounted( () => {
    initGRecaptcha()
})
</script>

<style lang="scss">

</style>
